import React, { useEffect } from "react";
import { useState } from "react";
import {
  Autocomplete,
  Button,
  Card,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { getVegetablePrice } from "../../../redux/reducer/vegetablePrice/vegetablePriceReducer";
import { postDataWithParams } from "../../../service/Service";
import NumberFormatting from "../../helper/NumberFormatting";
import { getPurchaseOrderCustById, purchaseOrderCustActions, updatePurchaseOrderCust } from "../../../redux/reducer/purchaseOrderCust/purchaseOrderCustReducer";
import { getCustomer } from "../../../redux/reducer/customer/customerReducer";

const useStyles = makeStyles(() => ({
  headerClass: {
    display: "flex",
    alignItems: "flex-start",
    width: "100%",
    flexDirection: "column",
    padding: "5px",
    margin: "10px 0px",
    background: "#F2F2F2",
  },
  dividerStyle: {
    border: "1px solid #386E5B",
    width: "100%",
    margin: "5px 0px",
  },
  footerButton: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "flex-end",
    marginBottom: "10px",
    width: "99%",
  },
  tableHeader: {
    background: "#ddd",
    textAlign: "center",
    borderBottom: "1px solid #D1CDCD",
    padding: "1em",
  },
  tableCell: {
    background: "#ddd",
    borderBottom: "1px solid #D1CDCD",
    padding: "5px 0px",
  },
  actionButtonEdit: {
    backgroundColor: "#dde0f0",
    color: "#A0A3B3",
    fontSize: "25px",
    textAlign: "center",
    borderRadius: "5px",
    margin: "auto",
    cursor: "pointer",
    "&:hover": {
      background: "#7dbda9",
    },
  },
}));

export default function AdminPurchaseOrderCustEdit() {
  // Navigate
  const { id } = useParams();
  const navigate = useNavigate();

  // Styling
  const styles = useStyles();

  // Redux Data
  const dispatch = useDispatch();
  const token = useSelector((state) => state.auth.token);
  const user = useSelector((state) => state.auth.dataToken);
  const customer = useSelector((state) => state.customer.data);
  const purchaseOrder = useSelector((state) => state.purchaseOrderCust.data);

  // Local State
  const [formData, setFormData] = useState({
    purchaseOrderCode: "",
    customerId: "",
    totalPrices: "",
    status: "",
  });
  const [vegetablePrice, setVegetablePrice] = useState([]);
  const [orderDetails, setOrderDetails] = useState([]);
  const [fetch, setFetch] = useState(true);

  useEffect(() => {
    if (fetch) {
      const x = purchaseOrder.docs.findIndex((item) => item._id === id);
      if (x > -1) {
        setFormData({
          purchaseOrderCode: purchaseOrder.docs[x].purchaseOrderCode
            ? purchaseOrder.docs[x].purchaseOrderCode
            : "",
          customerId: purchaseOrder.docs[x].customerId
            ? purchaseOrder.docs[x].customerId._id
            : "",
          customerName: purchaseOrder.docs[x].customerId
            ? purchaseOrder.docs[x].customerId.customerName
            : "",
          totalPrices: purchaseOrder.docs[x].totalPrices
            ? purchaseOrder.docs[x].totalPrices
            : "",
          status: purchaseOrder.docs[x].status
            ? purchaseOrder.docs[x].status
            : "",
        });
        let tempOrderDetails = [];
        let tempAutoComplete = [];
        purchaseOrder.docs[x].orderDetails.map((item) => {
          let data = {
            vegetablePriceId: item.vegetablePriceId._id,
            vegetablePriceName: item.vegetablePriceId.vegetableName,
            order: item.order,
            price: item.price,
          };
          tempAutoComplete.push(item.vegetablePriceId)
          tempOrderDetails.push(data);
        });
        setOrderDetails(tempOrderDetails);
        setAutoCompleteValue(tempAutoComplete)
      }
      setFetch(false);
    }
  }, [fetch]);

  useEffect(() => {
    if (formData.customerId.length > 0) {
      let body = {};
      let params = {
        paginate: false,
      };
      postDataWithParams("/master/vegetable-price/search", token, body, params)
        .then((res) => {
          if (res.data.status === 200) {
            let custIndex = customer.docs.findIndex((item) => item._id === formData.customerId)
            let customerType = res.data.data.docs.filter(
              (item) => item.priceTypeId?._id === customer.docs[custIndex].priceTypeId._id
            );
            setVegetablePrice(customerType);
          } else {
            toast.error(res.data.message);
          }
        })
        .catch((err) => {
          toast.error(err.response.message);
        });
      }
  }, [formData.customerId]);

  // Basic Handle Change
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Handling Order Details
  const handleOrderDetails = (index) => (e) => {
    const newItems = [...orderDetails];
    newItems[index][e.target.name] = e.target.value;
    newItems[index]["vegetablePriceId"] = autoCompleteValue[index]._id;
    newItems[index]["totalOrder"] = newItems[index]["order"];

    newItems[index]["price"] =
      newItems[index]["totalOrder"] * autoCompleteValue[index].price;
    setFormData({
      ...formData,
      totalPrices: newItems.reduce(
        (acc, cur) => parseInt(acc) + parseInt(cur.price),
        0
      ),
    });
    setOrderDetails(newItems);
  };

  const handleAddOrderDetails = () => {
    setOrderDetails((prevItems) => [
      ...prevItems,
      {
        vegetablePriceId: "",
        order: "",
        price: "",
      },
    ]);
  };

  const handleDeleteOrderDetails = (value) => {
    let deletedData = [];
    for (let i = 0; i < orderDetails.length; i++) {
      if (i !== value) {
        deletedData.push(orderDetails[i]);
      }
    }
    setOrderDetails(deletedData);
  };

  const handleSubmitAll = (e) => {
    e.preventDefault();
    Swal.fire({
      title: `Mohon dicek kembali data yang akan di input`,
      icon: "warning",
      buttons: true,
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Tambah",
      cancelButtonText: "Batal",
    }).then((confirm) => {
      if (confirm.isConfirmed) {
        let data = {
          value: {
            purchaseOrderCode: formData.purchaseOrderCode,
            customerId: formData.customerId,
            orderDetails: orderDetails,
            totalPricess: formData.totalPrices,
            status: formData.status,
            createdBy: user.username,
            updatedBy: user.username,
          },
          id: id,
          token: token,
        };
        dispatch(updatePurchaseOrderCust(data)).then((res) => {
          if (res.payload.status === 200) {
            let dataGetById = {
              id: res.payload.data._id,
              token: token,
            };
            dispatch(getPurchaseOrderCustById(dataGetById)).then((result) => {
              dispatch(
                purchaseOrderCustActions.editPurchaseOrderCust(result.payload.data)
              );
              toast.success(res.payload.message);
              navigate("/admin/po-toko");
            });
          }
        });
      } else {
        return;
      }
    });
  };

  const loadMore = (value) => (event) => {
    if (
      event.target.scrollHeight - event.target.scrollTop < 300 &&
      event.target.scrollTop < 100
    ) {
      if (customer.totalDocs !== customer.docs.length && value === 1) {
        const getData = {
          params: {
            limit: customer.limit + 10,
            page: 1,
          },
          token: token,
        };
        dispatch(getCustomer(getData));
      } else if (
        vegetablePrice.totalDocs !== vegetablePrice.docs.length &&
        value === 2
      ) {
        const getData = {
          params: {
            limit: vegetablePrice.limit + 10,
            page: 1,
          },
          token: token,
        };
        dispatch(getVegetablePrice(getData));
      }
    }
  };

  const orderDetailsColumn = [
    "HARGA SAYURAN",
    "ORDER",
    "HARGA",
    "AKSI",
  ];

  const [autoCompleteValue, setAutoCompleteValue] = useState([]);

  const handleVegetablePrice = (e, index) => {
    let tempVegetablePrice = [...autoCompleteValue];
    tempVegetablePrice[index] = e;
    setAutoCompleteValue(tempVegetablePrice);
  };

  return (
    <Grid container>
      <ToastContainer />
      <Card className={styles.headerClass}>
        <Typography fontSize={20} fontWeight={600} color={"#0A543B"}>
          EDIT PO TOKO
        </Typography>
      </Card>
      <Card className={styles.headerClass}>
        <form
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            marginTop: "10px",
          }}
          onSubmit={handleSubmitAll}
        >
          <FormControl
            style={{
              display: "flex",
              alignItems: "center",
              justifyItems: "flex-start",
              width: "100%",
              margin: "10px 0px",
              flexDirection: "row",
              background: "none",
            }}
          >
            <Typography
              fontSize={14}
              color={"#2e473f"}
              variant="h6"
              width="16%"
            >
              KODE NOTA TOKO
            </Typography>
            <TextField
              InputLabelProps={{ shrink: true }}
              inputProps={{ maxLength: 20, style: { background: "white" }, readOnly: true }}
              required
              helperText={
                formData.purchaseOrderCode.length > 0
                  ? /^[A-Za-z]{2}-\d{5}/.test(formData.purchaseOrderCode)
                    ? ""
                    : "*Format Kode Petani salah"
                  : ""
              }
              size="small"
              error={
                formData.purchaseOrderCode.length > 0
                  ? /^[A-Za-z]{2}-\d{5}/.test(formData.purchaseOrderCode)
                    ? false
                    : true
                  : false
              }
              name="purchaseOrderCode"
              onChange={handleChange}
              value={formData.purchaseOrderCode}
              fullWidth
              variant="outlined"
              style={{
                maxWidth: "400px",
                marginRight: "10px",
              }}
            />
          </FormControl>
          <FormControl
            style={{
              display: "flex",
              alignItems: "center",
              justifyItems: "flex-start",
              width: "100%",
              marginBottom: "10px",
              flexDirection: "row",
            }}
          >
            <Typography
              fontSize={14}
              color={"#2e473f"}
              variant="h6"
              width="16%"
            >
              CUSTOMER
            </Typography>
            <TextField
              select
              InputLabelProps={{ shrink: true }}
              inputProps={{ readOnly: true}}
              required
              size="small"
              error={false}
              name="customerId"
              onChange={handleChange}
              value={formData.customerId}
              fullWidth
              variant="outlined"
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    onScroll: loadMore(1),
                  },
                  style: {
                    height: "300px",
                  },
                },
              }}
              style={{
                maxWidth: "400px",
                background: "white",
                marginRight: "10px",
              }}
            >
              {formData.customerName && formData.customerId.length > 0 && (
                <MenuItem value={formData.customerId} key={0}>
                  {formData.customerName}
                </MenuItem>
              )}
              {customer.docs.length > 0 ? (
                customer.docs.map((data, index) => (
                  <MenuItem value={data._id} key={index}>
                    {data.customerName}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="">Data kosong</MenuItem>
              )}
            </TextField>
          </FormControl>
          <Divider />
          <Typography
            marginTop={1}
            marginBottom={1}
            fontSize={14}
            color={"#2e473f"}
            variant="h6"
          >
            ORDER DETAIL
          </Typography>
          <table
            style={{
              width: "100%",
              marginBottom: "10px",
              borderCollapse: "collapse",
              borderRadius: "1em",
              overflow: "hidden",
            }}
          >
            <thead>
              {orderDetailsColumn.map((item, index) => {
                return (
                  <td className={styles.tableHeader} key={index}>
                    {item}
                  </td>
                );
              })}
            </thead>
            <tbody>
              {orderDetails.length > 0 &&
                orderDetails.map((item, index) => {
                  return (
                    <tr key={index}>
                      <td className={styles.tableCell}>
                        <FormControl
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyItems: "flex-start",
                            flexDirection: "row",
                            margin: "auto",
                            width: "90%",
                          }}
                        >
                          <Autocomplete
                            id="auto-complete"
                            autoComplete
                            fullWidth
                            value={autoCompleteValue[index]}
                            onChange={(event, newValue) => {
                              handleVegetablePrice(newValue, index);
                            }}
                            style={{width: "400px"}}
                            options={vegetablePrice}
                            getOptionLabel={(option) => `${option.vegetableName} - ${option.unitWeight.unitWeightId.unitName}`}
                            includeInputInList
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                name="vegetablePriceId"
                                style={{
                                  width: "400px",
                                  maxWidth: "700px",
                                  background: "white",
                                  marginRight: "10px",
                                }}
                              />
                            )}
                          />
                        </FormControl>
                      </td>

                      <td className={styles.tableCell}>
                        <FormControl
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyItems: "flex-start",
                            flexDirection: "row",
                            margin: "auto",
                            width: "90%",
                          }}
                        >
                          <TextField
                            InputLabelProps={{ shrink: true }}
                            required
                            size="small"
                            InputProps={{
                              inputComponent: NumberFormatting,
                              endAdornment: (
                                <InputAdornment position="end">
                                  kg
                                </InputAdornment>
                              ),
                            }}
                            error={false}
                            name="order"
                            id={index}
                            onChange={handleOrderDetails(index)}
                            value={item.order}
                            variant="outlined"
                            fullWidth
                            style={{
                              background: "white",
                              marginRight: "10px",
                            }}
                          />
                        </FormControl>
                      </td>

                      <td className={styles.tableCell}>
                        <FormControl
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyItems: "flex-start",
                            flexDirection: "row",
                            margin: "auto",
                            width: "90%",
                          }}
                        >
                          <TextField
                            InputLabelProps={{ shrink: true }}
                            InputProps={{ readOnly: true }}
                            required
                            size="small"
                            error={false}
                            name="price"
                            id={index}
                            onChange={handleOrderDetails(index)}
                            value={item.price.toLocaleString("id", {
                              style: "currency",
                              currency: "IDR",
                            })}
                            variant="outlined"
                            fullWidth
                            style={{
                              width: '250px',
                              background: "white",
                              marginRight: "10px",
                            }}
                          />
                        </FormControl>
                      </td>

                      <td className={styles.tableCell}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "auto",
                            width: "100px",
                          }}
                        >
                          <AddIcon
                            className={styles.actionButtonEdit}
                            onClick={() => handleAddOrderDetails()}
                          />
                          {orderDetails.length > 1 && index !== 0 ? (
                            <RemoveIcon
                              className={styles.actionButtonEdit}
                              onClick={() => handleDeleteOrderDetails(index)}
                            />
                          ) : (
                            ""
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>

          <Divider />

          <FormControl
            style={{
              display: "flex",
              alignItems: "center",
              justifyItems: "flex-start",
              width: "100%",
              margin: "10px 0px",
              flexDirection: "row",
              background: "none",
            }}
          >
            <Typography
              fontSize={14}
              color={"#2e473f"}
              variant="h6"
              width="16%"
            >
              TOTAL ORDERS
            </Typography>
            <TextField
              InputLabelProps={{ shrink: true }}
              inputProps={{ readOnly: true, style: { background: "white" } }}
              required
              size="small"
              error={false}
              name="totalPrices"
              onChange={handleChange}
              value={formData.totalPrices.toLocaleString("id", {
                style: "currency",
                currency: "IDR",
              })}
              fullWidth
              variant="outlined"
              style={{
                maxWidth: "400px",
                marginRight: "10px",
              }}
            />
          </FormControl>

          <div className={styles.footerButton}>
            <Button variant="contained" type="submit">
              SUBMIT
            </Button>
            <Button
              variant="contained"
              color="error"
              style={{ marginLeft: "10px" }}
              onClick={() => navigate("/admin/po-toko")}
            >
              KEMBALI
            </Button>
          </div>
        </form>
      </Card>
    </Grid>
  );
}
